import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Trash2, Plus, UserCheck } from 'lucide-react';
import { useAccount, useSwitchChain } from 'wagmi';
import { resolveConnectedProvider } from '@coti-io/coti-wallet-plugin';
import { getChainConfig, getRpcUrlForChain } from '@/chains';
import { POD_PORTAL_FACTORY_ABI } from '@/contracts/pod';
import { BRIDGE_ABI, BRIDGE_ERC20_ABI } from '@/contracts/config';
import { formatAdminError } from '@/lib/formatAdminError';
import { useCotiPluginContext } from '@/context/PrivacyBridgeContext';

interface OperatorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bridgeName: string;
  bridgeAddress: string;
  isNativeBridge: boolean;
  networkChainId: number;
}

const OperatorsModal = ({
  isOpen,
  onClose,
  bridgeName,
  bridgeAddress,
  isNativeBridge,
  networkChainId,
}: OperatorsModalProps) => {
  const [operators, setOperators] = useState<string[]>([]);
  const [newAddress, setNewAddress] = useState('');
  const [isLoadingOperators, setIsLoadingOperators] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Connector/address of the wallet connected via RainbowKit — signing must go
  // through its provider, not window.ethereum, which another installed wallet
  // extension may own and pop up despite never being connected to the app.
  const { connector, address } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { switchNetwork } = useCotiPluginContext();

  const chainCfg = getChainConfig(networkChainId);
  const isPod = chainCfg?.portalStrategy === 'pod-privacy-portal';
  const factoryAddress = chainCfg?.addresses.PrivacyPortalFactory;
  const targetAddress = isPod ? (factoryAddress || '') : bridgeAddress;

  const getAbi = () => {
    if (isPod) return POD_PORTAL_FACTORY_ABI;
    return isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
  };

  const getSigner = async () => {
    if (!address) throw new Error('Connect your wallet first');
    const eip1193 = await resolveConnectedProvider(connector);
    if (!eip1193) throw new Error('No wallet provider found');
    let provider = new ethers.BrowserProvider(eip1193);
    const network = await provider.getNetwork();
    if (Number(network.chainId) !== networkChainId) {
      try {
        await switchChainAsync({ chainId: networkChainId });
      } catch {
        const switched = await switchNetwork('0x' + networkChainId.toString(16));
        if (!switched) {
          throw new Error('Please switch your wallet to the portal chain first');
        }
      }
      provider = new ethers.BrowserProvider(eip1193);
    }
    return provider.getSigner(address);
  };

  // Fetch operators using eth_getLogs for RoleGranted/RoleRevoked events
  // then verify live state with isOperator() — works on RPCs that don't support queryFilter.
  // Public RPCs (Infura/etc.) reject wide block ranges, so we chunk lookups.
  const fetchOperators = async () => {
    if (!targetAddress) {
      setError(isPod ? 'PrivacyPortalFactory address not configured.' : 'Bridge address missing.');
      setOperators([]);
      return;
    }
    setIsLoadingOperators(true);
    setError(null);
    try {
      const rpcUrl = getRpcUrlForChain(networkChainId);
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const contract = new ethers.Contract(targetAddress, getAbi(), provider);

      // Prefer on-chain OPERATOR_ROLE(); fall back to keccak256("OPERATOR_ROLE")
      let OPERATOR_ROLE: string;
      try {
        OPERATOR_ROLE = await contract.OPERATOR_ROLE();
      } catch {
        OPERATOR_ROLE = ethers.keccak256(ethers.toUtf8Bytes('OPERATOR_ROLE'));
      }

      // RoleGranted(bytes32 indexed role, address indexed account, address indexed sender)
      const roleGrantedTopic = ethers.id('RoleGranted(bytes32,address,address)');
      const roleRevokedTopic = ethers.id('RoleRevoked(bytes32,address,address)');

      const latest = await provider.getBlockNumber();
      // Infura Sepolia rejects ranges ≫ ~10k blocks; stay under that.
      const CHUNK = 9_000;
      // Cover ~1M blocks (~5 months on Sepolia) — enough for current factory deploys.
      const LOOKBACK = 1_000_000;
      const fromBlock = Math.max(0, latest - LOOKBACK);

      const grantedLogs: { topics: string[] }[] = [];
      const revokedLogs: { topics: string[] }[] = [];

      for (let start = fromBlock; start <= latest; start += CHUNK) {
        const end = Math.min(latest, start + CHUNK - 1);
        const range = {
          address: targetAddress,
          fromBlock: '0x' + start.toString(16),
          toBlock: '0x' + end.toString(16),
        };
        const [granted, revoked] = await Promise.all([
          provider.send('eth_getLogs', [{ ...range, topics: [roleGrantedTopic, OPERATOR_ROLE] }]),
          provider.send('eth_getLogs', [{ ...range, topics: [roleRevokedTopic, OPERATOR_ROLE] }]),
        ]);
        grantedLogs.push(...granted);
        revokedLogs.push(...revoked);
      }

      // account is the 3rd topic (index 2), zero-padded to 32 bytes
      const extractAddress = (log: { topics: string[] }) =>
        ethers.getAddress('0x' + log.topics[2].slice(26));

      const candidates = new Set([
        ...grantedLogs.map(extractAddress),
        ...revokedLogs.map(extractAddress),
      ]);

      // Verify live state for each candidate
      const checks = await Promise.all(
        [...candidates].map(async (addr) => ({
          addr,
          isOp: await contract.isOperator(addr).catch(() => false),
        }))
      );

      setOperators(checks.filter((c) => c.isOp).map((c) => c.addr));
    } catch (err) {
      console.error('fetchOperators error:', err);
      setError('Failed to load operators.');
    } finally {
      setIsLoadingOperators(false);
    }
  };

  useEffect(() => {
    if (isOpen && targetAddress) fetchOperators();
  }, [isOpen, targetAddress, networkChainId]);

  const handleAdd = async () => {
    if (!newAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      setError('Invalid address');
      return;
    }
    setError(null);
    setIsProcessing(true);
    try {
      const signer = await getSigner();
      const contract = new ethers.Contract(targetAddress, getAbi(), signer);
      let tx;
      if (isPod) {
        const role = await contract.OPERATOR_ROLE();
        tx = await contract.grantRole(role, newAddress);
      } else {
        tx = await contract.addOperator(newAddress);
      }
      await tx.wait();
      setNewAddress('');
      await fetchOperators();
    } catch (err) {
      setError(formatAdminError(err, 'Failed to add operator'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = async (opAddress: string) => {
    setError(null);
    setIsProcessing(true);
    try {
      const signer = await getSigner();
      const contract = new ethers.Contract(targetAddress, getAbi(), signer);
      let tx;
      if (isPod) {
        const role = await contract.OPERATOR_ROLE();
        tx = await contract.revokeRole(role, opAddress);
      } else {
        tx = await contract.removeOperator(opAddress);
      }
      await tx.wait();
      await fetchOperators();
    } catch (err) {
      setError(formatAdminError(err, 'Failed to remove operator'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setNewAddress('');
    setError(null);
    setOperators([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass sm:max-w-[560px] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-glow">Set Operators</DialogTitle>
          <DialogDescription>
            {isPod ? (
              <>
                Manage factory-wide operators for all portals on this chain
                {bridgeName ? (
                  <>
                    {' '}(opened from <span className="text-foreground">{bridgeName}</span>)
                  </>
                ) : null}
                . Roles apply to every PrivacyPortal instance, not just one row.
              </>
            ) : (
              <>
                Manage operators for <span className="text-foreground">{bridgeName}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2 min-w-0">
          {/* Current operators list */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Current Operators</Label>
            {isLoadingOperators ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading operators...
              </div>
            ) : operators.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No operators found</p>
            ) : (
              <div className="space-y-2">
                {operators.map((addr) => (
                  <div key={addr} className="flex items-center justify-between bg-muted/40 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-primary shrink-0" />
                      <code className="text-xs text-foreground break-all">{addr}</code>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                      onClick={() => handleRemove(addr)}
                      disabled={isProcessing}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add operator */}
          <div className="space-y-2">
            <Label htmlFor="newOperator">Add Operator</Label>
            <div className="flex gap-2">
              <Input
                id="newOperator"
                placeholder="0x..."
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                className="glass"
                disabled={isProcessing}
              />
              <Button
                onClick={handleAdd}
                disabled={isProcessing || !newAddress}
                className="bg-primary hover:bg-primary/90 shrink-0"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="glass overflow-hidden max-w-full">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <AlertDescription className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
                {error}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OperatorsModal;
